import { type RequestContext } from '@cx-web/domain-types';
import { type IndexUiState } from 'instantsearch.js';

import { type ComponentTag } from '~/apps/product-plus/constants/component-tags';

import { type BettingInsightsContent, type EsIndex } from '../types';

import { type BettingInsightsCardConfig, createBettingInsightsCard } from './BettingInsightsCard';
import { type BettingInsightsRefinementsConfig, createBettingInsightsRefinements } from './BettingInsightsRefinements';
import { type BettingInsightsResultsConfig, createBettingInsightsResults } from './BettingInsightsResults';
import { type BettingInsightsSearchConfig, createBettingInsightsSearch } from './BettingInsightsSearch';
import { type BettingInsightsSortingConfig, createBettingInsightsSorting } from './BettingInsightsSorting';

export interface StyleSheet {
  [key: string]: string;
}

export interface BettingInsightsUIContext {
  environment: RequestContext['environment'];
  featureFlags: RequestContext['featureFlags'];
  repubConfig: RequestContext['repubConfig'];
  tags: ComponentTag[];
  user: RequestContext['user'];
}

// Input config type (what users provide)
export interface BettingInsightsUIConfig<TIndex extends EsIndex, TContent extends BettingInsightsContent<TIndex>> {
  card: BettingInsightsCardConfig<TIndex, TContent>;
  initialUiState?: (context: BettingInsightsUIContext) => IndexUiState;
  refinements: BettingInsightsRefinementsConfig<TIndex>;
  results: BettingInsightsResultsConfig;
  search?: BettingInsightsSearchConfig<TIndex>;
  sorting: BettingInsightsSortingConfig<TIndex>;
}

// Type alias for the return type of createBettingInsightsUIConfig
export type BettingInsightsUI<TIndex extends EsIndex, TContent extends BettingInsightsContent<TIndex>> = ReturnType<
  typeof createBettingInsightsUIConfig<TIndex, TContent>
>;

export function createBettingInsightsUIConfig<TIndex extends EsIndex, TContent extends BettingInsightsContent<TIndex>>(
  requestContext: RequestContext,
  config: BettingInsightsUIConfig<TIndex, TContent>,
  tags: ComponentTag[],
) {
  // Create parent config for nested components
  // const notInUse = createParentConfig(requestContext, tags);

  // Create context object that will be passed to initialUiState
  const context: BettingInsightsUIContext = {
    environment: requestContext.environment,
    featureFlags: requestContext.featureFlags,
    repubConfig: requestContext.repubConfig,
    user: requestContext.user,
    tags,
  };

  // Create result object with wrapper method that injects context as parameter
  const result = {
    // UI config properties (nested configs)
    card: createBettingInsightsCard(requestContext, config.card),
    initialUiState: config.initialUiState ? () => config.initialUiState!(context) : undefined,
    refinements: createBettingInsightsRefinements(requestContext, config.refinements),
    results: createBettingInsightsResults(requestContext, config.results),
    search: config.search ? createBettingInsightsSearch(requestContext, config.search) : undefined,
    sorting: createBettingInsightsSorting(requestContext, config.sorting),
  };

  // Return immutable plain object
  return Object.freeze(result);
}
